import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { CSS } from '@dnd-kit/utilities';

import folderImage from '../../../assets/images/folder.png';
import { Asset } from '../../../app/entities/asset';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { getModifier } from '../../../core/services/event.service';
import { useResponsive } from '../../../hooks/useResponsive';
import { useSelection } from '../../../core/contexts/selection.context';
import { RenameDialog } from '../../../components/dialogs/rename.dialog';
import { catchError } from '../../../core/catch-error';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { openModal } from '../../../core/modal/open-modal';

import styles from './folder-item.module.scss';
type FolderItemProps = {
  folder: Asset;
  assetsRouteTo: (asset: string, assetId: string, folderName?: string) => void;
};

export const FolderItem: React.FC<FolderItemProps> = observer(({ folder, assetsRouteTo }) => {
  const { isMobile } = useResponsive();

  const {
    setNodeRef: draggableRef,
    attributes,
    listeners,
    transform,
  } = useDraggable({
    id: folder._id,
    data: {
      type: 'folder',
    },
  });

  const styles = {
    transform: CSS.Transform.toString(transform),
  };

  const { setNodeRef, isOver } = useDroppable({
    id: folder._id,
    data: {
      type: 'folder',
    },
  });

  const { select, elements, selectionModeOn } = useSelection();

  const handleRenameFolder = async (name: string) => {
    try {
      await folder.update({ name });
    } catch (e) {
      catchError(e);
    }
  };

  const menuItems = [
    // {
    //   onSelect: () => {},
    //   title: 'Share',
    //   icon: <ShareFile />,
    // },
    {
      onSelect: () =>
        openModal(RenameDialog, {
          defaultName: folder.name,
          onSubmit: handleRenameFolder,
          title: 'Rename folder',
          label: 'Folder name',
        }),
      title: 'Rename',
      icon: <Pencil1Icon />,
    },
    {
      onSelect: () =>
        openModal(DeleteDialog, {
          onSubmit: () => folder.delete(),
          text: `Are you sure you want to delete this ${folder.name} ?`,
          title: 'Delete folder',
        }),
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
    },
  ];

  const handleOpenFolder = () => assetsRouteTo('folder', folder._id);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isMobile || selectionModeOn) {
      const modifier = getModifier(e.nativeEvent);
      select(folder._id, modifier);
      return;
    }

    handleOpenFolder();
  };

  const isSelected = useMemo(() => elements.includes(folder._id), [elements, folder._id]);

  return (
    <div
      className={styles['container']}
      onClick={handleClick}
      onDoubleClick={handleOpenFolder}
      key={`${folder?._id}key`}
      data-selected={isSelected}
      ref={(e) => {
        setNodeRef(e);
        draggableRef(e);
      }}
      style={{ ...styles, ...(isOver ? { opacity: 0.5 } : {}) }}
      {...attributes}
      {...listeners}
    >
      <div className={styles['folder-link']}>
        <div className={styles['folder-image']}>
          <img src={folderImage} alt="Folder" />
        </div>

        <div className={styles['folder-name']}>{folder.name}</div>
      </div>

      <div className={styles['modal-container']}>
        <DropdownMenu items={menuItems} />
      </div>
    </div>
  );
});
